<template>
  <div>
    <template v-if="isLoading">
      <SkeletonLoader
        class="loader-skeleton"
        data-testid="loader-skeleton-buttons"
      >
        <div
          v-for="(button, index) in actionButtons"
          :key="index"
          class="loader-button"
        />
      </SkeletonLoader>
    </template>
    <template v-else>
      <WealthChartAction
        v-for="(button, index) in actionButtons.filter(
          button => button.isVisible
        )"
        :key="index"
        class="wealth-chart-action"
        :disabled="!button.isEnabled"
        :label="button.title"
        :data-testid="`wealth-action-${button.testId}`"
        @click="button.action"
      />
    </template>
  </div>
</template>

<script lang="ts">
import {
  Component,
  Vue,
  Emit,
  Prop,
  InjectReactive
} from 'vue-property-decorator';
import { FeatureFlags } from '@/types/models/FeatureFlags';
import { FeatureFlagsModule } from '@/modules/common/store/FeatureFlagsModule';
import { WealthChartAction } from '@/modules/wealth/components/WealthChartAction';
import {
  getWealthActionButtons,
  IActionButton
} from '@/modules/wealth/services/wealth-actions';
import { SkeletonLoader } from '@warrenbrasil/nebraska-web';
import { trackWealthWithdraw } from '../../track-events';

@Component({
  components: { WealthChartAction, SkeletonLoader }
})
export default class WealthViewActions extends Vue {
  @Prop({ type: Boolean, required: true })
  private isCustomerOverdue!: boolean;

  private isLoading = false;

  private isError = false;

  private actionButtonsBff: IActionButton[] = [];

  private actionButtons = [
    {
      title: 'Criar carteira',
      icon: 'EF0010',
      isEnabled: !this.isDisabledWarrenBuilder,
      isVisible: !this.isNewHomeInvestmentFlowEnabled,
      action: this.emitCreatePortfolioEvent,
      style: 'primary',
      testId: 'create-wealth'
    },
    {
      title: 'Investir',
      icon: 'BA0010',
      isEnabled: true,
      isVisible: true,
      action: this.goToInvestmentFlow,
      style: 'secondary',
      testId: 'invest'
    }
  ];

  @Emit('create-portfolio')
  private emitCreatePortfolioEvent() {}

  @Emit('open-investment')
  private emitInvestmentEvent() {}

  @InjectReactive('shouldTriggerHomeAnimations')
  public addLoadedClass!: boolean;

  created() {
    this.withdrawButton();
  }

  get isDisabledWarrenBuilder() {
    return FeatureFlagsModule.isEnabled(FeatureFlags.DISABLE_WARREN_BUILDER);
  }

  buttonsMapper() {
    if (this.hasWithdrawButton)
      this.actionButtons.push({
        title: this.actionButtonsBff[0].title,
        icon: 'EA0050',
        isEnabled: true,
        isVisible: true,
        action: this.goToWithdrawFlow,
        style: 'secondary',
        testId: 'withdraw'
      });
  }

  get hasWithdrawButton() {
    return this.actionButtonsBff.length > 0;
  }

  private async withdrawButton() {
    try {
      this.isLoading = true;
      const response = await getWealthActionButtons();
      this.actionButtonsBff = response.wealthButtons.data;
      this.buttonsMapper();
    } catch (error) {
      this.isError = true;
    } finally {
      this.isLoading = false;
    }
  }

  goToInvestmentFlow() {
    this.emitInvestmentEvent();

    if (this.isCustomerOverdue) {
      return this.$router.push({
        name: 'regulatoryUpdateAnnouncement'
      });
    }

    this.$router.push(
      this.isNewHomeInvestmentFlowEnabled
        ? {
            name: 'investment',
            query: {
              cameFrom: 'wealth'
            }
          }
        : { name: 'wealthInvest' }
    );
  }

  get isNewHomeInvestmentFlowEnabled() {
    return FeatureFlagsModule.isEnabled(
      FeatureFlags.ENABLE_NEW_HOME_INVESTMENT_FLOW
    );
  }

  goToWithdrawFlow() {
    trackWealthWithdraw();
    this.$router.push({
      name: 'wealthWithdraw'
    });
  }
}
</script>

<style lang="less" scoped>
.loader-skeleton {
  display: flex;
}

.loader-button {
  width: 156px;
  height: 48px;
  margin-right: @size-spacing-x450;
  border-radius: @size-radius-x200;
}

.wealth-chart-action {
  margin-right: @size-spacing-x350;
  margin-bottom: @size-spacing-x350;
}
</style>
